import { euMember } from 'is-european'
import _ from 'lodash'
import {
  AdjustCartParams,
  CartPaymentParams,
  SetUserDataParams,
  SignInParams,
  SubscriberInterface,
  UpdatePrivacyConsentParams,
  ViewItemParams,
} from '../../hooks/events/events.types'
import CartInterface, { CartItemInterface } from '../../interfaces/CartInterface'
import CategoryInterface from '../../interfaces/CategoryInterface'
import ChannelInterface from '../../interfaces/ChannelInterface'
import PrivacyConsentInterface, { PrivacyConsentsFieldEnum } from '../../interfaces/PrivacyConsentInterface'
import ProductInterface, { PriceInterface } from '../../interfaces/ProductInterface'
import { sha256 } from '../../utils/crypto'
import { getPrivacyConsents } from '../../utils/privacyConsent'

// Extend window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any
  }
}

type Ga4ItemCategories = {
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
}

type Ga4Item = Ga4ItemCategories & {
  item_id: string
  item_name: string
  item_variant?: string
  price: number
  quantity: number
  google_business_vertical: 'retail'
}

const fromBreadcrumbs = (breadcrumbs?: CategoryInterface[]): Ga4ItemCategories => {
  const result: Ga4ItemCategories = {}
  if (!breadcrumbs || breadcrumbs.length <= 1) return result
  const items = breadcrumbs.map(item => item?.translations?.['en-AU']?.name).filter(Boolean) // filter out undefined/null values
  // skip index 0 as it is category root
  if (items[1]) result.item_category = items[1]
  if (items[2]) result.item_category2 = items[2]
  if (items[3]) result.item_category3 = items[3]
  if (items[4]) result.item_category4 = items[4]
  if (items[5]) result.item_category5 = items[5]
  return result
}

const fromCartItem = (item: CartItemInterface): Ga4Item => {
  return {
    item_id: item.productId ? String(item.productId) : '',
    item_name: item.name || '',
    item_variant: item.skuCode || '',
    price: parseFloat(item.priceGross) || 0,
    quantity: item.quantity || 1,
    google_business_vertical: 'retail',
    ...fromBreadcrumbs(item?.product?.parent?.breadcrumbs),
  }
}

const fromCartItems = (cart: CartInterface) => {
  return cart.items.map(fromCartItem)
}

const fromCart = (cart: CartInterface) => {
  return {
    currency: cart.currency,
    value: cart.totalGross,
    items: fromCartItems(cart),
  }
}

const fromProduct = (product: ProductInterface, variantId: number, currency: string, quantity: number = 1): Ga4Item | undefined => {
  const variant: ProductInterface | undefined = _.find(product.variants, (_variant: ProductInterface): boolean => _variant.id === variantId)
  if (!variant) return undefined

  const price: PriceInterface | undefined = _.find(variant.pricing, (_price: PriceInterface): boolean => _price.currency === currency)

  return {
    item_id: variant.id ? String(variant.id) : '',
    item_name: product.translations?.['en-AU']?.name || '',
    item_variant: variant.skuCode || '',
    price: parseFloat(price?.grossPrice || '0') || 0,
    quantity: quantity || 1,
    google_business_vertical: 'retail',
    ...fromBreadcrumbs(product.breadcrumbs),
  }
}

/**
 * GA4 tracker using GTM dataLayer instead of gtag
 */
class GA4Tracker implements SubscriberInterface {
  constructor(
    private trackingId: string,
    private sendTo: string[],
    private countryCode: string,
  ) {}

  private pushToDataLayer = (data: any): void => {
    if (!window.dataLayer) {
      window.dataLayer = []
    }

    // Debug: log items with missing item_id
    if (data.items && Array.isArray(data.items)) {
      const itemsWithMissingId = data.items.filter((item: any) => !item.item_id || item.item_id === 'undefined' || item.item_id === 'null')
      if (itemsWithMissingId.length > 0) {
        console.warn('GA4 Tracker: Items with missing item_id detected:', itemsWithMissingId)
      }
    }

    window.dataLayer.push(data)
  }

  init = (): void => {
    const consents = getPrivacyConsents()
    this.updatePrivacyConsents({
      type: 'default',
      consents: {
        [PrivacyConsentsFieldEnum.REQUIRED]: true,
        [PrivacyConsentsFieldEnum.MARKETING]: euMember(this.countryCode) ? consents?.[PrivacyConsentsFieldEnum.MARKETING] ?? false : true,
        [PrivacyConsentsFieldEnum.FUNCTIONAL]: euMember(this.countryCode) ? consents?.[PrivacyConsentsFieldEnum.FUNCTIONAL] ?? false : true,
      },
      wait_for_update: 500,
    })
  }

  switchChannel = (channel?: ChannelInterface): void => {
    this.pushToDataLayer({
      event: 'switch_shop',
      targetShop: channel?.theme!,
    })
  }

  showChannel = (channel?: ChannelInterface): void => {
    if (!channel) return
    const { id, theme, domain, path, type } = channel
    this.pushToDataLayer({
      event: 'set_channel_data',
      wfChannelId: id,
      wfChannelTheme: theme,
      wfChannelDomain: domain,
      wfChannelPath: path,
      wfChannelUrl: 'https://' + domain + path,
      wfChannelType: type,
    })
  }

  signIn = (data: SignInParams): void => {
    this.pushToDataLayer({
      event: 'login',
      login_path: data.pathname,
      method: data.method,
    })
  }

  signUp = (data: SignInParams): void => {
    this.pushToDataLayer({
      event: 'sign_up',
      login_path: data.pathname,
      method: data.method,
    })
  }

  clickCheckout = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'begin_checkout',
      ...fromCart(cart),
    })
  }

  viewCart = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'view_cart',
      ...fromCart(cart),
    })
  }

  adjustCart = (data: AdjustCartParams): void => {
    if (data.adjQty === 0) return
    const eventName = data.adjQty > 0 ? 'add_to_cart' : 'remove_from_cart'
    let value = 0
    let items: Ga4Item[] = []

    // TODO: consider report parent product only
    if (data.item) {
      value = parseFloat(data.item.costGross) || 0
      items = [fromCartItem(data.item)]
    } else if (data.product && data.variantId) {
      const ga4Item = fromProduct(data.product, data.variantId, data.currency, data.adjQty)
      if (!ga4Item) return
      items = [ga4Item]
      value = ga4Item.price * ga4Item.quantity
    } else {
      return
    }

    this.pushToDataLayer({
      event: eventName,
      currency: data.currency,
      value,
      items,
    })
  }

  viewItem = (data: ViewItemParams): void => {
    const ga4Item = fromProduct(data.product, data.product.variants[0].id, data.currency)
    if (!ga4Item) return
    this.pushToDataLayer({
      event: 'view_item',
      currency: data.currency,
      value: ga4Item.price * ga4Item.quantity,
      items: [ga4Item],
    })
  }

  viewCartAuth = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'view_cart_auth',
      ...fromCart(cart),
    })
  }

  viewCartShipping = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'view_cart_shipping',
      ...fromCart(cart),
    })
  }

  viewCartPayment = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'view_cart_payment',
      ...fromCart(cart),
    })
  }

  addShippingInfo = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'add_shipping_info',
      ...fromCart(cart),
      shipping_tier: cart.shippingMethod?.name || '',
    })
  }

  clickPayment = (cart: CartInterface): void => {
    this.pushToDataLayer({
      event: 'click_payment',
      ...fromCart(cart),
    })
  }

  addPaymentInfo = (data: CartPaymentParams): void => {
    this.pushToDataLayer({
      event: 'add_payment_info',
      ...fromCart(data.cart),
      payment_type: data.method || '',
    })
  }

  purchase = (order: CartInterface): void => {
    this.pushToDataLayer({
      event: 'purchase',
      ...fromCart(order),
      transaction_id: order.id,
      shipping: order.shippingCostGross,
      tax: order.totalTax,
      coupon: _.isEmpty(order.discounts) ? undefined : order.discounts.map(discount => discount.code).join(';'),
      affiliation: 'Brandshop',
    })
  }

  setUserData = (data: SetUserDataParams): void => {
    this.pushToDataLayer({
      event: 'set_user_data',
      user_data: {
        sha256_email_address: sha256(data.user.email),
        ...(data.address.phoneNumber != null ? { sha256_phone_number: sha256(data.address.phoneNumber) } : {}),
        address: {
          sha256_first_name: sha256(data.user.firstName),
          sha256_last_name: sha256(data.user.lastName),
          street: data.address.street! + (data.address.street2 != null ? `, ${data.address.street2}` : ''),
          city: data.address.city,
          postal_code: data.address.postCode,
          country: data.address.country,
          region: data.address.state,
        },
      },
    })
  }

  updatePrivacyConsents = (data: UpdatePrivacyConsentParams): void => {
    if (!gtag) return
    // Push consent update to dataLayer
    gtag('consent', data.type, {
      ad_storage: data.consents[PrivacyConsentsFieldEnum.MARKETING] ? 'granted' : 'denied',
      ad_user_data: data.consents[PrivacyConsentsFieldEnum.MARKETING] ? 'granted' : 'denied',
      ad_personalization: data.consents[PrivacyConsentsFieldEnum.MARKETING] ? 'granted' : 'denied',
      analytics_storage: data.consents[PrivacyConsentsFieldEnum.FUNCTIONAL] ? 'granted' : 'denied',
      wait_for_update: data.wait_for_update,
    })

    // Also push the consent event for tracking
    this.pushToDataLayer({
      event: 'wfConsentUpdate',
    })
  }

  updateMetaPixelConsents = (data: PrivacyConsentInterface): void => {
    // Push pixel consent data to dataLayer for Meta Pixel
    this.pushToDataLayer({
      event: 'set_pixel_consent',
      pixelFunctionalConsent: data[PrivacyConsentsFieldEnum.FUNCTIONAL],
    })
  }
}

export default (countryCode: string, trackingId?: string): SubscriberInterface => {
  if (!trackingId) return {} as SubscriberInterface // return phantom object if no tracking id
  return new GA4Tracker(trackingId, ['GA4'], countryCode)
}
